import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button, Card, Col, Form, Input, Row, Select, Spin, message,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined, SendOutlined } from '@ant-design/icons';
import { EmailHtmlEditor } from '../../components/email-html-editor/EmailHtmlEditor';
import { useEmailTemplates, useEmailTemplateDetail } from '../../hooks/useEmailTemplates';
import type { CreateEmailTemplateDto, UpdateEmailTemplateDto, SendTestEmailDto } from '../../services/email-template.service';

const STATE_ACTIVE = 1;
const STATE_INACTIVE = 0;

// Params available for drag-drop (from sample reference)
const TEMPLATE_PARAMS: Record<string, string> = {
  customer_name: 'Tên khách hàng',
  customer_email: 'Email khách hàng',
  customer_phone: 'Số điện thoại',
  order_code: 'Mã đơn hàng',
  order_total: 'Tổng tiền đơn hàng',
  order_date: 'Ngày đặt hàng',
  order_status: 'Trạng thái đơn hàng',
  delivery_date: 'Ngày giao hàng',
  shipping_method: 'Phương thức giao hàng',
  shipping_address: 'Địa chỉ nhận hàng',
  product_name: 'Tên sản phẩm',
  product_list: 'Danh sách sản phẩm',
  store_name: 'Tên cửa hàng',
  store_phone: 'SĐT cửa hàng',
  support_email: 'Email hỗ trợ',
};

function extractVariables(text: string): string[] {
  const matches = text.match(/\{\{([^}]+)\}\}/g) ?? [];
  return [...new Set(matches.map((m) => m.slice(2, -2)))];
}

export const EmailTemplateEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const [form] = Form.useForm();
  const [content, setContent] = useState('');
  const [draggedParam, setDraggedParam] = useState<string | null>(null);
  const cursorPosRef = useRef<number>(0);
  const [testEmail, setTestEmail] = useState('');
  const [testVariables, setTestVariables] = useState<Record<string, string>>({});
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Hooks
  const { createTemplate } = useEmailTemplates();
  const { template, loading: loadingDetail, sendTest, updateTemplate: updateTemplateDetail } = useEmailTemplateDetail(id);

  // Populate form when editing
  useEffect(() => {
    if (template) {
      form.setFieldsValue({
        name: template.name,
        code: template.code,
        subject: template.subject,
        state: template.state,
      });
      setContent(template.content);
      setWatchedSubject(template.subject ?? '');
    }
  }, [template, form]);

  const [watchedSubject, setWatchedSubject] = useState('');
  const allVariables = useMemo(() => {
    return extractVariables(watchedSubject + ' ' + content);
  }, [watchedSubject, content]);

  useEffect(() => {
    setTestVariables((prev) => {
      const next: Record<string, string> = {};
      allVariables.forEach((v) => { next[v] = prev[v] ?? ''; });
      return next;
    });
  }, [allVariables]);

  // Drag handlers
  const handleDragStart = useCallback((e: React.DragEvent, param: string) => {
    setDraggedParam(param);
    e.dataTransfer.setData('text/plain', param);
    e.dataTransfer.effectAllowed = 'copy';
  }, []);

  const handleDragEnd = useCallback(() => setDraggedParam(null), []);

  // Drop into subject input
  const handleSubjectDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const param = e.dataTransfer.getData('text/plain');
    if (!param) return;
    const currentSubject = (form.getFieldValue('subject') as string) ?? '';
    const pos = cursorPosRef.current;
    const newValue = currentSubject.slice(0, pos) + `{{${param}}}` + currentSubject.slice(pos);
    form.setFieldsValue({ subject: newValue });
    setWatchedSubject(newValue);
    setDraggedParam(null);
  }, [form]);

  // Save handler
  const handleSave = async () => {
    try {
      const values = await form.validateFields() as { name: string; code: string; subject: string; state: number };
      setIsSaving(true);
      const payload = { ...values, content };
      if (isEdit) {
        await updateTemplateDetail(payload as UpdateEmailTemplateDto);
        message.success('Cập nhật thành công');
      } else {
        await createTemplate(payload as CreateEmailTemplateDto);
        message.success('Tạo template thành công');
      }
      navigate('/email-templates');
    } catch (err) {
      if (err && typeof err === 'object' && 'errorFields' in err) return;
      message.error('Lưu thất bại');
    } finally {
      setIsSaving(false);
    }
  };

  // Send test handler
  const handleSendTest = useCallback(async () => {
    if (!testEmail.trim()) {
      message.warning('Nhập email người nhận');
      return;
    }
    setIsSendingTest(true);
    try {
      const subject = form.getFieldValue('subject') as string ?? '';
      let processedSubject = subject;
      let processedContent = content;
      Object.entries(testVariables).forEach(([k, v]) => {
        const re = new RegExp(`\\{\\{${k}\\}\\}`, 'g');
        processedSubject = processedSubject.replace(re, v);
        processedContent = processedContent.replace(re, v);
      });
      const dto: SendTestEmailDto = {
        recipient_email: testEmail,
        subject: processedSubject,
        content: processedContent,
      };
      await sendTest(dto);
      message.success('Gửi test email thành công');
    } catch {
      message.error('Gửi test email thất bại');
    } finally {
      setIsSendingTest(false);
    }
  }, [testEmail, testVariables, content, form, sendTest]);

  if (isEdit && loadingDetail) {
    return <div style={{ textAlign: 'center', padding: 50 }}><Spin size="large" /></div>;
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/email-templates')}>
          Quay lại
        </Button>
      </div>

      <Form
        form={form}
        layout="vertical"
        onValuesChange={(changed: Record<string, string>) => {
          if ('subject' in changed) setWatchedSubject(changed.subject ?? '');
        }}
      >
        {/* Row 1: Thông tin cơ bản + Gửi test */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={16}>
            <Card title="Thông tin cơ bản">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="name"
                    label="Tên template"
                    rules={[{ required: true, message: 'Nhập tên template' }]}
                  >
                    <Input placeholder="Nhập tên template" maxLength={200} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="code"
                    label="Mã template"
                    rules={[{ required: true, message: 'Nhập mã template' }]}
                  >
                    <Input placeholder="vd: order_confirm" maxLength={100} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="state" label="Trạng thái" initialValue={STATE_INACTIVE} style={{ marginBottom: 0 }}>
                    <Select options={[
                      { value: STATE_ACTIVE, label: 'Hoạt động' },
                      { value: STATE_INACTIVE, label: 'Không hoạt động' },
                    ]} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          <Col span={8}>
            <Card title="Gửi test email" style={{ height: '100%' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <div style={{ marginBottom: 4, fontWeight: 500 }}>Email người nhận</div>
                  <Input
                    type="email"
                    placeholder="Nhập email để gửi test"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                  />
                </div>
                {allVariables.length > 0 && (
                  <div>
                    {allVariables.map((variable) => (
                      <div key={variable} style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#888', marginBottom: 2 }}>{`{{${variable}}}`}</div>
                        <Input
                          placeholder={`Giá trị cho ${variable}`}
                          value={testVariables[variable] ?? ''}
                          onChange={(e) => setTestVariables((prev) => ({ ...prev, [variable]: e.target.value }))}
                        />
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    loading={isSendingTest}
                    disabled={!testEmail.trim()}
                    onClick={handleSendTest}
                  >
                    Gửi test
                  </Button>
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Row 2: Nội dung full width */}
        <Card title="Nội dung" style={{ marginBottom: 16 }}>
          <Form.Item label="Tiêu đề email" required style={{ marginBottom: 16 }}>
            <div
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
              onDrop={handleSubjectDrop}
            >
              <Form.Item
                name="subject"
                noStyle
                rules={[{ required: true, message: 'Nhập tiêu đề email' }]}
              >
                <Input
                  placeholder="Nhập tiêu đề email"
                  maxLength={200}
                  style={draggedParam ? { background: '#f0f5ff' } : undefined}
                  onFocus={(e) => { cursorPosRef.current = e.target.selectionStart ?? 0; }}
                  onBlur={(e) => { cursorPosRef.current = e.target.selectionStart ?? 0; }}
                  onClick={(e) => { cursorPosRef.current = (e.target as HTMLInputElement).selectionStart ?? 0; }}
                  onKeyUp={(e) => { cursorPosRef.current = (e.currentTarget as HTMLInputElement).selectionStart ?? 0; }}
                />
              </Form.Item>
            </div>
          </Form.Item>

          <Row gutter={12} align="top">
            <Col flex="auto">
              <Form.Item label="Nội dung email" required style={{ marginBottom: 0 }}>
                <EmailHtmlEditor
                  content={content}
                  onChangeContent={setContent}
                  draggedParam={draggedParam}
                />
              </Form.Item>
            </Col>
            <Col style={{ width: 240 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Tham số có sẵn</div>
              <p style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>
                Kéo thả vào tiêu đề hoặc nội dung
              </p>
              <div
                style={{
                  height: 500,
                  overflowY: 'auto',
                  border: '1px solid #f0f0f0',
                  borderRadius: 4,
                  padding: 8,
                  background: draggedParam ? '#f0f5ff' : '#fafafa',
                }}
              >
                {Object.entries(TEMPLATE_PARAMS).map(([param, label]) => (
                  <div
                    key={param}
                    draggable
                    onDragStart={(e) => handleDragStart(e, param)}
                    onDragEnd={handleDragEnd}
                    style={{
                      cursor: 'grab',
                      border: '1px solid #d9d9d9',
                      borderRadius: 4,
                      padding: '6px 10px',
                      marginBottom: 6,
                      background: '#fff',
                      userSelect: 'none',
                    }}
                  >

                    <div style={{ fontSize: 13 }}>{label}</div>
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        </Card>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={isSaving}
            onClick={handleSave}
          >
            Lưu
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default EmailTemplateEdit;
